import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Camera, ClipboardCheck, Download, Gem, Plus } from "lucide-react";
import { api, statusOf } from "@/lib/api";
import { useHub } from "@/components/AppShell";
import { EmptyState, Loader, NoAccess, PageHeader } from "@/components/common";
import { FormDialog } from "@/components/kids/kidsUi";
import { Checkbox } from "@/components/ui/checkbox";
import { useLiveRefresh } from "@/lib/live";
import { writesLocked } from "@/lib/splits";
import ItemDialog from "@/components/inventory/ItemDialog";
import { Thumb, download, money } from "@/components/inventory/util";

const RGB = "var(--glow-home)";
const MISS = { photo: "photo", receipt: "receipt", serial: "serial number", value: "value" };

function Checklist({ base, onClose, onOpen }) {
  const [c, setC] = useState(null);
  useEffect(() => { api.get(`${base}/checklist`).then(({ data }) => setC(data)); }, [base]);
  return (
    <FormDialog open onClose={onClose} title="Claim-ready checklist" testid="checklist-dialog">
      {!c ? <Loader /> : (
        <div className="space-y-3">
          <p className="font-display text-4xl font-extrabold text-q-text" data-testid="checklist-percent">{c.percent}%</p>
          <p className="text-sm text-q-muted">Photos {c.by_check.photo}% · Receipts {c.by_check.receipt}% · Serials {c.by_check.serial}% · Values {c.by_check.value}%</p>
          <p className="text-sm" data-testid="checklist-policy">Policy on record: <b>{c.policy_on_record ? "Yes" : "No"}</b> · Policy file: <b>{c.policy_file_on_record ? "Yes" : "No"}</b></p>
          <ul className="space-y-1">{c.items_missing.map((i) => (
            <li key={i.id}><button className="min-h-[44px] w-full rounded-lg px-2 text-left text-sm text-q-text hover:bg-q-raised" onClick={() => onOpen(i.id)} data-testid={`checklist-item-${i.id}`}><b>{i.name}</b> <span className="text-q-muted">({i.room}) missing {i.missing.map((m) => MISS[m]).join(", ")}</span></button></li>
          ))}</ul>
        </div>
      )}
    </FormDialog>
  );
}

function Export({ base, d, onClose }) {
  const [scope, setScope] = useState("all");
  const [room, setRoom] = useState(d.rooms[0]?.id);
  const [ids, setIds] = useState([]);
  const [busy, setBusy] = useState(false);
  const go = async (format) => { setBusy(true); await download(`${base}/export`, { scope, room_id: room, item_ids: ids, format }, `inventory.${format}`); setBusy(false); };
  return (
    <FormDialog open onClose={onClose} title="Export a claim" description="PDF, spreadsheet, or a ZIP with both plus the original photos and receipts by room." testid="export-dialog">
      <select className="q-input mb-2 min-h-[44px] w-full" value={scope} onChange={(e) => setScope(e.target.value)} data-testid="export-scope"><option value="all">All items</option><option value="room">One room</option><option value="selected">Selected items</option></select>
      {scope === "room" && <select className="q-input mb-2 min-h-[44px] w-full" value={room} onChange={(e) => setRoom(e.target.value)} data-testid="export-room">{d.rooms.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}</select>}
      {scope === "selected" && d.items.map((i) => <label key={i.id} className="flex min-h-[44px] items-center gap-3 text-sm"><Checkbox className="h-6 w-6" checked={ids.includes(i.id)} onCheckedChange={(v) => setIds(v ? [...ids, i.id] : ids.filter((x) => x !== i.id))} data-testid={`export-pick-${i.id}`} />{i.name}</label>)}
      <div className="mt-3 grid grid-cols-3 gap-2">{["pdf", "xlsx", "zip"].map((f) => <button key={f} className="q-btn q-btn-primary min-h-[44px]" disabled={busy || (scope === "selected" && !ids.length)} onClick={() => go(f)} data-testid={`export-${f}`}>{f.toUpperCase()}</button>)}</div>
    </FormDialog>
  );
}

function ItemCard({ base, i, onOpen }) {
  return (
    <button className="q-card q-rise flex min-h-[44px] min-w-0 items-center gap-3 p-3 text-left" onClick={onOpen} data-testid={`inv-item-${i.id}`}>
      <Thumb url={i.photos[0] && `${base}/items/${i.id}/photos/${i.photos[0].id}`} alt={i.name} className="h-16 w-16" />
      <span className="min-w-0 flex-1">
        <span className="block truncate font-semibold text-q-text">{i.name}</span>
        <span className="block truncate text-xs text-q-muted">{i.room} · {i.category}{i.valuable_type ? ` · ${i.valuable_type}` : ""}</span>
        {i.missing.length > 0 && <span className="block text-xs" style={{ color: "rgb(var(--glow-lists))" }}>Missing {i.missing.map((m) => MISS[m]).join(", ")}</span>}
      </span>
      <span className="font-display font-bold text-q-text" data-testid={`inv-item-value-${i.id}`}>{money(i.total_value)}</span>
    </button>
  );
}

export default function Inventory() {
  const { hub } = useHub();
  const base = `/organizations/${hub.id}/inventory`;
  const [sp, setSp] = useSearchParams();
  const [d, setD] = useState(null);
  const [denied, setDenied] = useState(false);
  const [room, setRoom] = useState("");
  const [val, setVal] = useState(false);
  const [open, setOpen] = useState(null);
  const [check, setCheck] = useState(false);
  const [exp, setExp] = useState(false);
  const locked = writesLocked(hub);
  const load = useCallback(() => api.get(base, { params: { room, valuables: val } }).then(({ data }) => setD(data)).catch((e) => statusOf(e) === 403 && setDenied(true)), [base, room, val]);
  useEffect(() => { load(); }, [load]);
  useLiveRefresh(load, (m) => m.entity === "inventory", 30000);
  useEffect(() => { if (sp.get("snap") && d && !locked) setOpen({ quick: true, preset: { name: sp.get("name") || "", value: sp.get("value") || "" } }); }, [sp, d, locked]);
  if (denied) return <NoAccess />;
  if (!d) return <Loader />;
  const close = () => { setOpen(null); if (sp.get("snap")) setSp({}); };
  const item = open?.id && d.items.find((x) => x.id === open.id);
  return (
    <div className="min-w-0" data-testid="inventory-page">
      <PageHeader eyebrow="Home" title="Inventory" rgb={RGB} testid="inventory-title" />
      <div className="mb-4 flex flex-wrap items-end gap-x-6 gap-y-2">
        <p className="font-display text-3xl font-extrabold text-q-text" data-testid="inventory-total">{money(d.total_value)}</p>
        <p className="text-sm text-q-muted" data-testid="inventory-count">{d.count} item{d.count === 1 ? "" : "s"}</p>
      </div>
      <div className="mb-4 flex flex-wrap gap-2">
        <button className="q-btn q-btn-primary min-h-[44px]" disabled={locked} onClick={() => setOpen({ quick: true })} data-testid="snap-btn"><Camera className="h-4 w-4" /> Snap a new purchase</button>
        <button className="q-btn q-btn-secondary min-h-[44px]" disabled={locked} onClick={() => setOpen({})} data-testid="add-item-btn"><Plus className="h-4 w-4" /> Add item</button>
        <button className="q-btn q-btn-secondary min-h-[44px]" onClick={() => setCheck(true)} data-testid="checklist-btn"><ClipboardCheck className="h-4 w-4" /> Claim-ready</button>
        {d.is_parent && <button className="q-btn q-btn-secondary min-h-[44px]" onClick={() => setExp(true)} data-testid="export-btn"><Download className="h-4 w-4" /> Export claim</button>}
        <button className={`q-btn min-h-[44px] ${val ? "q-btn-primary" : "q-btn-ghost"}`} aria-pressed={val} onClick={() => setVal(!val)} data-testid="valuables-filter"><Gem className="h-4 w-4" /> Valuables</button>
      </div>
      <div className="mb-4 flex gap-2 overflow-x-auto pb-1" data-testid="room-tabs">
        <button className={`q-btn min-h-[44px] shrink-0 ${!room ? "q-btn-primary" : "q-btn-ghost"}`} onClick={() => setRoom("")} data-testid="room-all">All rooms</button>
        {d.rooms.map((r) => <button key={r.id} className={`q-btn min-h-[44px] shrink-0 ${room === r.id ? "q-btn-primary" : "q-btn-ghost"}`} onClick={() => setRoom(r.id)} data-testid={`room-${r.name.replace(/\W+/g, "-").toLowerCase()}`}>{r.name} <span className="text-xs opacity-80" data-testid={`room-total-${r.name.replace(/\W+/g, "-").toLowerCase()}`}>{r.count} · {money(r.value)}</span></button>)}
      </div>
      {!d.items.length ? <EmptyState title="Nothing here yet" hint="Snap a photo of what you own, room by room." rgb={RGB} testid="inventory-empty" /> : (
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3" data-testid="inventory-items">{d.items.map((i) => <ItemCard key={i.id} base={base} i={i} onOpen={() => setOpen({ id: i.id })} />)}</div>
      )}
      {open && (!open.id || item) && <ItemDialog key={open.id || "new"} base={base} d={d} item={item} preset={open.preset} quick={open.quick} locked={locked} onClose={close}
        onSaved={(x, stay) => { load(); if (!stay) close(); }} />}
      {check && <Checklist base={base} onClose={() => setCheck(false)} onOpen={(id) => { setCheck(false); setOpen({ id }); }} />}
      {exp && <Export base={base} d={d} onClose={() => setExp(false)} />}
    </div>
  );
}
